import { OvCard, OvFormProvider, PageWrapper } from '@components'
import { LABELS, SETTINGS_NAVIGATION, TOOLTIPS } from '@constants'
import { useStore } from '@hooks/useStore'
import { Error } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Collapse,
  FormHelperText,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material'
import { IOVErrorResponseData, ISingleCompany } from '@typings'
import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  BillingSettingsForm,
  EmailSettingsForm,
  InvoiceSettingsForm,
  MyCompanyFormGeneral,
  QuotationSettingsForm,
} from 'src/partials/index.partials'
import { useIsMobile } from 'src/utilities/hooks/useIsMobile'
import { toast } from 'react-toastify'
import { yupResolver } from '@hookform/resolvers/yup'
import { myCompanyValidationSchema } from 'src/partials/forms/my-company/validation/my-company.form.validation'

export const SettingsView = observer(() => {
  const { company } = useStore()
  const { hash, state, search } = useLocation()
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  const [formError, setFormError] = useState<string | null>()
  const [activeSection, setActiveSection] = useState(() => {
    // Check for tab query parameter first (e.g., ?tab=email)
    const params = new URLSearchParams(search)
    const tabParam = params.get('tab')
    if (tabParam) {
      const exists = SETTINGS_NAVIGATION.find(item => item.value === tabParam)
      if (exists) return tabParam
    }

    // Check if hash exists in URL and matches a valid navigation item
    if (hash) {
      const value = hash.replace('#', '')
      const exists = SETTINGS_NAVIGATION.find(item => item.value === value)
      if (exists) return value
    }
    return 'bedrijf'
  })

  const getBackAction = useMemo(() => {
    if (!state?.from) return undefined
    return () => navigate(-1)
  }, [])

  const getDefaultValues = async () => {
    const _company = await company.getMyCompany().catch(() => void 0)
    return { company: _company }
  }

  const form = useForm({
    resolver: yupResolver(myCompanyValidationSchema()),
    defaultValues: getDefaultValues,
    mode: 'onChange',
  })

  const handleSubmit = () => {
    form.handleSubmit(onSubmit)()
  }

  const handleCancel = () => {
    form.reset()
  }

  const handleSuccess = (data: ISingleCompany) => {
    toast('Instellingen opgeslagen', { type: 'success' })
    form.reset({ company: data })
    setFormError(null)
  }

  const handleFormErrors = (data: IOVErrorResponseData) => {
    const { message, errors } = data

    if (message) setFormError(message)

    if (errors) {
      const errorKeys = Object.keys(errors)

      errorKeys.map(singleKey =>
        form.setError(singleKey as any, {
          type: 'custom',
          message: errors[singleKey][0],
        })
      )
    }
  }

  const onSubmit = async (values: FieldValues) => {
    setFormError(null)
    try {
      const res = company.has_company ? await company.update(values.company) : await company.create(values.company)
      handleSuccess(res)
    } catch (e) {
      handleFormErrors(e as IOVErrorResponseData)
    }
  }

  const renderContent = () => {
    return (
      <Stack spacing={1}>
        <Collapse in={!!formError}>
          <Alert
            color="error"
            icon={<Error />}>
            <FormHelperText error>{formError}</FormHelperText>
          </Alert>
        </Collapse>
        <OvCard>
          <Stack spacing={4}>
            <Typography variant="h3">
              {SETTINGS_NAVIGATION.find(item => item.value === activeSection)?.label}
            </Typography>
            {activeSection === 'bedrijf' && <MyCompanyFormGeneral />}
            {activeSection === 'facturen' && <InvoiceSettingsForm />}
            {activeSection === 'offertes' && <QuotationSettingsForm />}
            {activeSection === 'email' && <EmailSettingsForm />}
            {activeSection === 'subscription' && <BillingSettingsForm />}
          </Stack>
        </OvCard>
      </Stack>
    )
  }

  return (
    <OvFormProvider
      form={form}
      onSubmit={onSubmit}
      onCancel={handleCancel}
      loading={company.loading}>
      <PageWrapper
        pageTitle={LABELS.SETTINGS}
        actions={
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              width: isMobile ? '100%' : 'auto',
            }}>
            <Tooltip title={TOOLTIPS.CANCEL_ESC}>
              <Box sx={{ flex: isMobile ? 1 : 0 }}>
                <Button
                  size="small"
                  variant="outlined"
                  type="reset"
                  onClick={handleCancel}
                  disabled={!form.formState.isDirty}
                  sx={{ width: isMobile ? '100%' : 'auto' }}>
                  {LABELS.CANCEL}
                </Button>
              </Box>
            </Tooltip>
            <Tooltip title={TOOLTIPS.SAVE_SETTINGS}>
              <Box sx={{ flex: isMobile ? 1 : 0 }}>
                <Button
                  size="small"
                  variant="contained"
                  type="submit"
                  onClick={handleSubmit}
                  disabled={!form.formState.isDirty}
                  sx={{ width: isMobile ? '100%' : 'auto' }}>
                  {LABELS.SETTINGS_SAVE}
                </Button>
              </Box>
            </Tooltip>
          </Box>
        }
        backAction={getBackAction}
        fixedActions={true}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {isMobile ? (
            /* Mobile: Horizontal Tabs */
            <Paper sx={{ width: '100%' }}>
              <Tabs
                value={activeSection}
                onChange={(_, newValue) => setActiveSection(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                }}>
                {SETTINGS_NAVIGATION.map(item => (
                  <Tab
                    key={item.value}
                    label={item.label}
                    value={item.value}
                  />
                ))}
              </Tabs>
            </Paper>
          ) : (
            /* Desktop: Sidebar */
            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* Sidebar */}
              <Paper
                sx={{
                  width: 220,
                  flexShrink: 0,
                  p: 1.5,
                }}>
                <List disablePadding>
                  {SETTINGS_NAVIGATION.map(item => (
                    <ListItem
                      key={item.value}
                      disablePadding
                      sx={{ mb: 0.5 }}>
                      <ListItemButton
                        selected={activeSection === item.value}
                        onClick={() => setActiveSection(item.value)}
                        sx={{
                          borderRadius: 1,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                            },
                            '& .MuiListItemIcon-root': {
                              color: 'primary.contrastText',
                            },
                          },
                        }}>
                        <ListItemIcon>
                          <item.icon />
                        </ListItemIcon>
                        <ListItemText primary={item.label} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>

              {/* Content */}
              <Box sx={{ flex: 1 }}>{renderContent()}</Box>
            </Box>
          )}

          {/* Mobile: Full-width content */}
          {isMobile && <Box sx={{ width: '100%' }}>{renderContent()}</Box>}
        </Box>
      </PageWrapper>
    </OvFormProvider>
  )
})
